<template>
  <div class="rooms-page">
    <b-card no-body class="main-card">
      <b-card-body class="p-3">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div>
            <div class="d-flex align-items-center mb-2">
              <b-button
                variant="outline-secondary"
                size="sm"
                class="mr-2"
                @click="goBack"
              >
                <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
                {{ $t("common.back") }}
              </b-button>
              <h2 class="mb-0">
                {{ floorName || $t("reception.rooms") }}
              </h2>
            </div>
            <p class="text-muted mb-0">
              {{ $t("reception.rooms-in-floor") }}
            </p>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <b-spinner :label="$t('common.loading')" />
        </div>

        <!-- Rooms Grid -->
        <div v-else-if="roomsData.length > 0">
          <b-row>
            <b-col
              v-for="room in roomsData"
              :key="room.id"
              cols="12"
              sm="6"
              md="4"
              lg="3"
              class="mb-3"
            >
              <b-card
                no-body
                :class="[
                  'room-card',
                  'h-100',
                  room.current_active_patient_card
                    ? 'room-occupied'
                    : 'room-empty',
                ]"
                :style="{
                  cursor: room.current_active_patient_card
                    ? 'pointer'
                    : 'default',
                }"
                @click="handleRoomClick(room)"
              >
                <div class="room-card-content">
                  <div class="room-header">
                    <div
                      :class="[
                        'room-icon-wrapper',
                        room.current_active_patient_card
                          ? 'room-icon-occupied'
                          : 'room-icon-empty',
                      ]"
                    >
                      <feather-icon
                        icon="HomeIcon"
                        size="24"
                        class="room-icon"
                      />
                    </div>

                    <span class="room-name">
                      {{ room.name }}
                    </span>
                  </div>
                  <div class="room-info">
                    <div
                      v-if="room.current_active_patient_card"
                      class="patient-info"
                    >
                      <span>{{ $t("lab.patient_name") }}:</span>
                      <p class="patient-name mb-0">
                        {{
                          (room.current_active_patient_card.patient &&
                            room.current_active_patient_card.patient.name) ||
                          $t("reception.patient")
                        }}
                      </p>
                      <div
                        v-if="
                          room.current_active_patient_card.surgeries &&
                          room.current_active_patient_card.surgeries.length > 0
                        "
                        class="surgery-info"
                      >
                        <div class="surgery-label">
                          <span class="text-muted small">{{ $t("common.surgery") }}:</span>
                        </div>
                        <div class="surgery-badges">
                          <b-badge
                            v-for="(surgeryItem, index) in room.current_active_patient_card.surgeries"
                            :key="index"
                            variant="light-info"
                            pill
                            class="surgery-badge"
                          >
                            {{ surgeryItem.surgery ? surgeryItem.surgery.title : 'N/A' }}
                          </b-badge>
                        </div>
                      </div>
                    </div>
                    <div v-else class="empty-room-indicator">
                      <p class="text-muted mb-0 small">
                        {{ $t("reception.empty-room") }}
                      </p>
                    </div>
                  </div>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <!-- Pagination -->
          <div v-if="totalRooms > 0" class="mt-4">
            <b-row>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-start"
              >
                <span class="text-muted">
                  {{ $t("common.showing") }} {{ (page - 1) * take + 1 }}
                  {{ $t("common.to") }}
                  {{ Math.min(page * take, totalRooms) }}
                  {{ $t("common.of") }} {{ totalRooms }}
                  {{ $t("reception.rooms") }}
                </span>
              </b-col>
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <b-pagination
                  v-model="page"
                  :total-rows="totalRooms"
                  :per-page="take"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </div>

        <!-- Empty State -->
        <b-card v-else no-body class="empty-state-card">
          <div class="text-center py-5">
            <feather-icon icon="HomeIcon" size="48" class="text-muted mb-2" />
            <p class="text-muted">
              {{ $t("reception.no-rooms-in-floor") }}
            </p>
          </div>
        </b-card>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BPagination,
  BBadge,
} from "bootstrap-vue";

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BPagination,
    BBadge,
  },
  data() {
    return {
      loading: true,
      roomsData: [],
      totalRooms: 0,
      page: 1,
      take: 100,
      floorId: null,
      floorName: "",
    };
  },
  watch: {
    page(newPage, oldPage) {
      if (oldPage !== undefined && newPage !== oldPage && !this.loading) {
        this.getRooms();
      }
    },
  },
  mounted() {
    this.floorId = this.$route.params.floorId;
    this.floorName = this.$route.query.floorName || "";
    this.getRooms();
  },
  methods: {
    async getRooms() {
      if (!this.floorId) {
        this.loading = false;
        return;
      }

      this.loading = true;
      try {
        const url = `/room?page=${this.page}&take=${this.take}&sort=-id&filter[floor_id]=${this.floorId}&include=floor,currentActivePatientCard`;
        await this.getList(url);
        this.totalRooms = this.all_data.total || 0;
        this.roomsData = this.all_data.data || [];
      } catch (error) {
        console.error("Error fetching rooms:", error);
        this.roomsData = [];
        this.totalRooms = 0;
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push({ name: "Reception-Floors" });
    },
    handleRoomClick(room) {
      if (room.current_active_patient_card) {
        const patientCard = room.current_active_patient_card;
        this.$router.push({
          name: "active-patient-card",
          params: {
            id: patientCard.id,
            is_active: patientCard.is_active ? "1" : "0",
            card_id: patientCard.id,
          },
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.rooms-page {
  padding: 1rem;
}

.main-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.room-card {
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  overflow: hidden;
}

.room-card.room-occupied {
  border-color: #10b981;
  border-width: 2px;
}

.room-card.room-empty {
  border-color: #d1d5db;
  background-color: #f9fafb;
}

.room-card.room-occupied:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.2);
  border-color: #059669;
}

.room-card.room-empty:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.room-card-content {
  padding: 1.5rem;
}

.room-header {
  display: flex;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
}

.room-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.room-icon-occupied {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.room-icon-empty {
  background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
}

.room-icon {
  color: #ffffff;
}

.room-badges {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.room-info {
  flex: 1;
}

.room-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
}

.patient-info {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.patient-name {
  font-size: 1.125rem;
  font-weight: 600;
  color: #059669;
  word-break: break-word;
}

.surgery-info {
  width: 100%;
  padding-top: 0.5rem;
  margin-top: 0.5rem;
  border-top: 1px solid #e5e7eb;
}

.surgery-label {
  margin-bottom: 0.5rem;
}

.surgery-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-start;
}

.surgery-badge {
  margin: 0 !important;
  word-break: break-word;
  max-width: 100%;
  white-space: normal;
  text-align: center;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.4;
}

.empty-room-indicator {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
}

.empty-state-card {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

/* Responsive Design */
@media (max-width: 768px) {
  .room-card-content {
    padding: 1.25rem;
  }

  .room-icon-wrapper {
    width: 40px;
    height: 40px;
  }

  .room-name {
    font-size: 1rem;
  }
}
</style>
